import { useQuery, useLazyQuery, gql } from "@apollo/client"
import * as Sentry from "@sentry/browser"

const JOB_STEP_FIELDS = gql`
  fragment JobStepFields on JobProcessStep {
    id
    step
    name
    description
    responseType
    response
    responseUploads {
      id
      key
      fileName
      fileGroup
      fileSize
    }
    skip
    actions
    unresolvedActions
    hasResolvedActions
    completedAt
    completedBy {
      id
      status
      fullName
      firstName
      lastName
      avatar {
        key
      }
      groups {
        id
        name
      }
      locations {
        id
        name
      }
      activeAt
    }
    uploads {
      id
      key
      fileName
      fileGroup
      fileSize
    }
    notes {
      id
      text
      author {
        id
        fullName
        firstName
        lastName
        avatar {
          key
        }
      }
      createdAt
    }
  }
`

const JOB_FIELDS = gql`
  ${JOB_STEP_FIELDS}
  fragment JobFields on Job {
    id
    title
    keywords
    process {
      id
      name
      repeat {
        type
        frequency
        weekdays {
          monday
          tuesday
          wednesday
          thursday
          friday
          saturday
          sunday
        }
      }
    }
    task {
      id
      name
      description
      priority
      uploads {
        id
        key
        fileName
        fileGroup
        fileSize
      }
    }
    availableFrom
    dueAt
    adhoc
    pinning {
      type
      until
    }
    priority
    percentComplete
    status {
      completed
      completedAt
      completedBy {
        id
        fullName
        firstName
        lastName
        avatar {
          key
        }
      }
      steps {
        ...JobStepFields
      }
    }
    users {
      id
      fullName
      firstName
      lastName
      avatar {
        key
      }
    }
    groups {
      id
      name
    }
    author {
      id
      fullName
      firstName
      lastName
      avatar {
        key
      }
      groups {
        id
      }
    }
    location {
      id
      timeZone
    }
    createdAt
    updatedAt
  }
`

const JOBS_QUERY = gql`
  ${JOB_FIELDS}
  query Jobs($location: ID!, $date: ScheduleDateInput!, $groups: [ID!]) {
    jobs(location: $location, date: $date, groups: $groups) {
      unconfirmed
      total
      items {
        ...JobFields
      }
    }
  }
`

const MENTIONABLE_JOBS_QUERY = gql`
  query MentionableJobs($locations: [ID!]!) {
    mentionableJobs(locations: $locations) {
      processes {
        id
        name
        jobs {
          id
          name
          process
          location {
            id
            name
            timeZone
          }
          locationDay
          date
        }
      }
      tasks {
        id
        name
        location {
          id
          name
          timeZone
        }
        locationDay
        date
      }
    }
  }
`

const ACTIVE_PROCESS_JOBS_QUERY = gql`
  query ActiveProcessJobs($process: ID!) {
    activeProcessJobs(process: $process) {
      id
    }
  }
`

const ACTIVE_TASK_JOBS_QUERY = gql`
  query ActiveTaskJobs($task: ID!) {
    activeTaskJobs(task: $task) {
      id
    }
  }
`

const JOB_CREATED_SUBSCRIPTION = gql`
  ${JOB_FIELDS}
  subscription jobCreated {
    jobCreated {
      ...JobFields
    }
  }
`
const JOB_UPDATED_SUBSCRIPTION = gql`
  ${JOB_FIELDS}
  subscription jobUpdated {
    jobUpdated {
      ...JobFields
    }
  }
`

const loadMore = (result) => async () => {
  try {
    await result.fetchMore({
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return {
          ...prev,
          jobs: {
            unconfirmed: prev.jobs.unconfirmed,
            total: prev.jobs.total,
            items: [...prev.jobs.items, ...fetchMoreResult.jobs.items],
            __typename: prev.jobs.__typename,
          },
        }
      },
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const subscribe = (result) => () =>
  [
    result.subscribeToMore({
      document: JOB_CREATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        const newItem = subscriptionData.data.jobCreated
        return {
          ...prev,
          jobs: {
            unconfirmed: prev.jobs.unconfirmed,
            total: prev.jobs.items.length + 1,
            items: [newItem, ...prev.jobs.items],
            __typename: prev.jobs.__typename,
          },
        }
      },
      onError: (err) => console.log("[useQueryJobs][JOB_CREATED_SUBSCRIPTION]", err),
    }),
    result.subscribeToMore({
      document: JOB_UPDATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        const updatedItem = subscriptionData.data.jobUpdated
        let job = prev.jobs?.items.find((p) => p.id === updatedItem.id)
        if (job) {
          job = { ...updatedItem }
        }
        return prev
      },
      onError: (err) => console.log("[useQueryJobs][JOB_CREATED_SUBSCRIPTION]", err),
    }),
  ]

const useQueryJobs = (options) => {
  const result = useQuery(JOBS_QUERY, {
    ...options,
  })

  return {
    ...result,
    loadMore: loadMore(result),
    subscribe: subscribe(result),
  }
}

const useLazyQueryJobs = (options) => {
  const [loadJobs, result] = useLazyQuery(JOBS_QUERY, {
    ...options,
  })

  return [
    loadJobs,
    {
      ...result,
      loadMore: loadMore(result),
      subscribe: subscribe(result),
    },
  ]
}

const useQueryMentionableJobs = (options) =>
  useQuery(MENTIONABLE_JOBS_QUERY, {
    fetchPolicy: "cache-and-network",
    ...options,
  })

const useLazyQueryActiveProcessJobs = (options) =>
  useLazyQuery(ACTIVE_PROCESS_JOBS_QUERY, {
    fetchPolicy: "cache-and-network",
    ...options,
  })

const useLazyQueryActiveTaskJobs = (options) =>
  useLazyQuery(ACTIVE_TASK_JOBS_QUERY, {
    fetchPolicy: "cache-and-network",
    ...options,
  })

const jobProcessStepTypePolicies = {
  JobProcessStep: {
    fields: {
      notes: {
        merge(prev, next) {
          return [...next]
        },
      },
    },
  },
}

export {
  useQueryJobs,
  useLazyQueryJobs,
  useQueryMentionableJobs,
  useLazyQueryActiveProcessJobs,
  useLazyQueryActiveTaskJobs,
  JOBS_QUERY,
  JOB_FIELDS,
  JOB_STEP_FIELDS,
  jobProcessStepTypePolicies,
}
